import { useFormContext } from 'react-hook-form';
import { Card, LocalizedModerateHighlight, OverlayedLoader } from '@mews-commander/ui';
import { FormCheckboxField, lt } from '@mews/framework';
import { TextKey, SearchType } from '@mews-commander/enums';
import { Banner, Button, Container, Stack, StackItem } from '@optimus-web/core';
import { AddressFormFields, SearchHookField } from '../../../components/form/hookFields';
import { EMPTY_ADDRESS_FORM_VALUES } from './constants';
import { addressFormValuesEq } from './utils';
import { useSelectedAccountAddress } from './hooks';
import { ChangeBillingDetailsFormValues } from './types';

const addAssociatedAccountPrefix = (value: string) => `associatedAccount.address.${value}`;

export const ChangeAssociatedAccountDetailsFields = () => {
    const { setValue, watch } = useFormContext<ChangeBillingDetailsFormValues>();
    const currentAssociatedAccountId = watch('associatedAccount.id');
    const { result, toggleResultSelectionFlag } = useSelectedAccountAddress(currentAssociatedAccountId);

    const handleClearFields = () => {
        toggleResultSelectionFlag(false);
        setValue('associatedAccount.address', EMPTY_ADDRESS_FORM_VALUES, { shouldDirty: true });
        setValue('associatedAccount.associatedAccountAddressObjection', false);
    };

    return (
        <Container position="relative">
            <Card>
                <OverlayedLoader show={result.isFetching} />
                <Stack spacing="300" vertical>
                    <LocalizedModerateHighlight textKey={TextKey.ChangeBillingDetailsAssociatedAccountSectionTitle} />
                    <SearchHookField
                        name="associatedAccount.id"
                        label={lt('SearchForCustomerOrCompany')}
                        searchTypes={[SearchType.Customer, SearchType.Company]}
                        onResultClear={handleClearFields}
                        disabled={result.isFetching}
                        newEntity
                        bottomCaption={lt('ChangeBillingDetailsAssociatedAccountSearchCaption')}
                        onResultClick={() => {
                            toggleResultSelectionFlag(true);
                        }}
                        data-test-id="associatedAccount.search"
                        size="medium"
                    />
                    {currentAssociatedAccountId ? (
                        <>
                            {result.isSuccess && addressFormValuesEq.equals(result.data, EMPTY_ADDRESS_FORM_VALUES) ? (
                                <Banner
                                    variant="warning"
                                    primaryText={lt('ChangeBillingDetailsNoAddressBannerTitle')}
                                    secondaryText={lt('ChangeBillingDetailsNoAddressBannerDescription')}
                                />
                            ) : null}
                            <FormCheckboxField name="associatedAccount.associatedAccountAddressObjection">
                                {lt('BillAssociatedAccountAddressObjection')}
                            </FormCheckboxField>
                            <AddressFormFields
                                transformFieldName={addAssociatedAccountPrefix}
                                transformDataTestId={addAssociatedAccountPrefix}
                                addressSearchAutocomplete
                                size="medium"
                            />
                            <StackItem>
                                <Button onClick={handleClearFields} size="medium" type="button">
                                    {lt('ClearAddress')}
                                </Button>
                            </StackItem>
                        </>
                    ) : null}
                </Stack>
            </Card>
        </Container>
    );
};
